import { rootApi } from '../rootApi';
import { cleanParams } from '../utils';

const transformOrderRequestList = (response) => ({
    items: Array.isArray(response?.data) ? response.data : []
});

export const vehicleOrderApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getVehicleOrderBrands: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetBrands',
                method: 'GET',
                params: cleanParams({ languageId })
            }),
            transformResponse: (res) => (res?.isSuccessful && Array.isArray(res?.data) ? res.data : [])
        }),
        getVehicleOrderModels: builder.query({
            query: ({ brand, languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetModels',
                method: 'GET',
                params: cleanParams({ Brand: brand, languageId })
            }),
            transformResponse: (res) => (res?.isSuccessful && Array.isArray(res?.data) ? res.data : [])
        }),
        getVehicleOrderMaterials: builder.query({
            query: ({ brand, model, languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetMaterials',
                method: 'GET',
                params: cleanParams({ Brand: brand, Model: model, languageId })
            }),
            transformResponse: (res) => (res?.isSuccessful && Array.isArray(res?.data) ? res.data : [])
        }),
        getVehicleOrderPaymentTerms: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetPaymentTerms',
                method: 'GET',
                params: cleanParams({ languageId })
            }),
            transformResponse: (res) => {
                if (!res?.isSuccessful || !Array.isArray(res?.data)) return [];
                return res.data.map((item) => ({
                    label: item.label ?? item.text ?? item.name ?? item.description ?? String(item.value ?? item.code ?? item.id ?? ''),
                    value: item.value ?? item.code ?? item.id ?? ''
                }));
            }
        }),
        getVehicleOrderInitialData: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetInitialData',
                method: 'GET',
                params: cleanParams({ languageId })
            }),
            transformResponse: (res) => (res?.isSuccessful && res?.data ? res.data : { currency: '' })
        }),
        createVehicleOrderDraft: builder.mutation({
            query: (body) => ({
                url: 'v1/VehicleOrder/CreateDraft',
                method: 'POST',
                body
            }),
            transformResponse: (res) => res
        }),
        getAllRequestCount: builder.query({
            query: () => ({
                url: 'v1/VehicleOrder/GetAllRequestCount',
                method: 'POST'
            }),
            transformResponse: (res) => ({
                draftCount: res?.data?.draftCount ?? 0,
                pendingApprovalCount: res?.data?.pendingApprovalCount ?? 0,
                approvedCount: res?.data?.approvedCount ?? 0,
                rejectedCount: res?.data?.rejectedCount ?? 0,
                allCount: res?.data?.allCount ?? 0
            }),
            providesTags: ['VehicleOrder']
        }),

        getDraftList: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetDraftList',
                method: 'GET',
                params: cleanParams({ languageId })
            }),
            transformResponse: transformOrderRequestList,
            providesTags: ['VehicleOrder']
        }),
        getPendingApprovalList: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetPendingApprovalList',
                method: 'GET',
                params: cleanParams({ languageId })
            }),
            transformResponse: transformOrderRequestList,
            providesTags: ['VehicleOrder']
        }),
        getApprovedList: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetApprovedList',
                method: 'GET',
                params: cleanParams({ languageId })
            }),
            transformResponse: transformOrderRequestList,
            providesTags: ['VehicleOrder']
        }),
        getRejectedList: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetRejectedList',
                method: 'GET',
                params: cleanParams({ languageId })
            }),
            transformResponse: transformOrderRequestList,
            providesTags: ['VehicleOrder']
        }),
        getAllOrderRequests: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetAllRequestList',
                method: 'GET',
                params: cleanParams({ languageId })
            }),
            transformResponse: transformOrderRequestList,
            providesTags: ['VehicleOrder']
        }),
        exportAllOrderRequestsToExcel: builder.query({
            query: ({ languageId, compressionType = 0 } = {}) => ({
                url: 'v1/VehicleOrder/ExportAllRequestsToExcel',
                method: 'POST',
                body: cleanParams({ languageId, compressionType })
            }),
            transformResponse: (response) => {
                if (!response?.data) {
                    throw new Error(response?.errorMessage || 'Export failed');
                }
                return response.data;
            }
        }),
        getOrderDetail: builder.query({
            query: ({ orderId, languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetDetail',
                method: 'GET',
                params: cleanParams({ orderId, languageId })
            }),
            transformResponse: (res) => (res?.isSuccessful ? res.data : null)
        }),
        getAvailableChassis: builder.query({
            query: ({ orderId, languageId } = {}) => ({
                url: 'v1/VehicleOrder/GetAvailableChassis',
                method: 'GET',
                params: cleanParams({ orderId, languageId })
            }),
            transformResponse: (res) => (res?.isSuccessful && Array.isArray(res?.data) ? res.data : [])
        }),

        submitForApproval: builder.mutation({
            query: (body) => ({
                url: 'v1/VehicleOrder/SubmitForApproval',
                method: 'POST',
                body
            }),
            transformResponse: (res) => res,
            invalidatesTags: ['VehicleOrder']
        }),
        updateDraft: builder.mutation({
            query: (body) => ({
                url: 'v1/VehicleOrder/UpdateDraft',
                method: 'PUT',
                body
            }),
            transformResponse: (res) => res,
            invalidatesTags: ['VehicleOrder']
        }),
        processApproval: builder.mutation({
            query: (body) => ({
                url: 'v1/VehicleOrder/ProcessApproval',
                method: 'POST',
                body
            }),
            transformResponse: (res) => res,
            invalidatesTags: ['VehicleOrder']
        }),
        approveByLogistics: builder.mutation({
            query: (body) => ({
                url: 'v1/VehicleOrder/ApproveByLogistics',
                method: 'POST',
                body
            }),
            transformResponse: (res) => res,
            invalidatesTags: ['VehicleOrder']
        }),

        getVehicleStockList: builder.query({
            query: (filters = {}) => ({
                url: 'v1/VehicleOrder/GetVehicleStockList',
                method: 'GET',
                params: cleanParams({
                    chassisNo: filters.chassisNo,
                    brand: filters.brand,
                    model: filters.model,
                    matnr: filters.matnr,
                    maktx: filters.maktx,
                    status: filters.status,
                    sellable: filters.sellable,
                    languageId: filters.languageId
                })
            }),
            transformResponse: (res) => (res?.isSuccessful && Array.isArray(res?.data) ? res.data : []),
            providesTags: ['VehicleOrder']
        }),
        updateVehicleStockSellable: builder.mutation({
            query: (body) => ({
                url: 'v1/VehicleOrder/UpdateVehicleStockSellable',
                method: 'POST',
                body
            }),
            transformResponse: (res) => res,
            invalidatesTags: ['VehicleOrder']
        }),
        sellVehicleStockToCustomer: builder.mutation({
            query: (body) => ({
                url: 'v1/VehicleOrder/SellVehicleStockToCustomer',
                method: 'POST',
                body
            }),
            transformResponse: (res) => res,
            invalidatesTags: ['VehicleOrder']
        })
    })
});

export const {
    useGetVehicleOrderBrandsQuery,
    useGetVehicleOrderModelsQuery,
    useLazyGetVehicleOrderModelsQuery,
    useGetVehicleOrderMaterialsQuery,
    useLazyGetVehicleOrderMaterialsQuery,
    useGetVehicleOrderPaymentTermsQuery,
    useGetVehicleOrderInitialDataQuery,
    useCreateVehicleOrderDraftMutation,
    useGetAllRequestCountQuery,
    useGetDraftListQuery,
    useLazyGetDraftListQuery,
    useGetPendingApprovalListQuery,
    useLazyGetPendingApprovalListQuery,
    useGetApprovedListQuery,
    useLazyGetApprovedListQuery,
    useGetRejectedListQuery,
    useLazyGetRejectedListQuery,
    useGetAllOrderRequestsQuery,
    useLazyGetAllOrderRequestsQuery,
    useLazyExportAllOrderRequestsToExcelQuery,
    useGetOrderDetailQuery,
    useLazyGetOrderDetailQuery,
    useGetAvailableChassisQuery,
    useLazyGetAvailableChassisQuery,
    useSubmitForApprovalMutation,
    useUpdateDraftMutation,
    useProcessApprovalMutation,
    useApproveByLogisticsMutation,
    useGetVehicleStockListQuery,
    useUpdateVehicleStockSellableMutation,
    useSellVehicleStockToCustomerMutation
} = vehicleOrderApi;
